import { computed, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
//Store
import { useAnnotationsStore } from 'Store/pages/imageReview/annotationsStore'
import { useImagesStore } from 'Store/pages/imageReview/imagesStore'
import { useMeasurementStore } from 'Store/pages/imageReview/measurementsStore'
import { useFilterStore } from 'Store/filterStore'
import { useErrorStore } from 'Store/errors/errorStore'
//API
import { useGetInspectionImagesFiltered } from 'API/queries/iris/inspection-image'
import { useImageAnnotationsFiltered } from 'API/queries/iris/annotation'
import { useGetImageMeasurementsFiltered } from 'API/queries/iris/measurement'
import { useUpdateReviewStateMutation } from 'API/mutations/iris/inspection-image'
import { useImageMeasurement } from 'API/subscriptions/image-measurement'
import {
  useCreateImageAnnotationMutation,
  useUpdateImageAnnotationMutation,
  useDeleteImageAnnotationMutation,
  usePostImageAnnotationGroup,
  usePatchImageAnnotationGroup,
  useDeleteAnnotationGroup,
  usePatchAnnotationsAndPostGroup,
  usePatchAnnotationsAndPatchGroup,
} from 'API/mutations/iris/annotation'
import {
  useCreateMeasurementMutation,
  useUpdateMeasurementMutation,
  useDeleteMeasurementMutation,
} from 'API/mutations/iris/measurement'
//Util
import { clone } from 'lodash'
//Types
import { JeteyeRoutes } from 'Modules/RouterSetup'
import {
  AnnotationTypes,
  AnnotationModalTypes,
  AnnotationsStatuses,
  MeasurementStatus,
} from 'Modules/types/pages/imageReview/enums'
import { ImageReviewStates } from 'Modules/types/generic/image/ImageReviewStates'
import { ImageAnnotationType } from 'Modules/types/pages/imageReview/imageAnnotationType'
import { ElNotification } from 'element-plus'

export const useServerStore = defineStore('ImageReview_Server', () => {
  //State
  const route = useRoute()
  const filterStore = useFilterStore()
  const errorStore = useErrorStore()
  const { queryParamObj, areFiltersReady } = storeToRefs(filterStore)
  const imageUuidParam = computed<string | string[] | undefined>(() => {
    if (route.name === JeteyeRoutes.ImageReview) return queryParamObj.value?.inspectionImage?.[0]
    return undefined
  })
  const inspectionInstanceUuidParam = computed<string | string[] | undefined>(() => {
    if (route.name === JeteyeRoutes.ImageReview) return queryParamObj.value?.inspectionInstance
    return undefined
  })
  const workcardDefinitionParam = computed<string | string[] | undefined>(() => {
    if (route.name === JeteyeRoutes.ImageReview) return queryParamObj.value?.workcardDefinition
    return undefined
  })
  const queriesEnabled = computed<boolean>(
    () =>
      areFiltersReady.value === true &&
      inspectionInstanceUuidParam.value !== undefined &&
      imageUuidParam.value !== undefined
  )
  const queryParams = computed(() => {
    return {
      inspectionInstance: [inspectionInstanceUuidParam.value],
      inspectionImage: [imageUuidParam.value],
      ...queryParamObj.value,
    }
  })

  const imageQueryParams = computed(() => {
    return {
      inspectionInstance: [inspectionInstanceUuidParam.value],
      workcardDefinition: [workcardDefinitionParam.value],
    }
  })

  const annotationsStore = useAnnotationsStore()

  const imagesStore = useImagesStore()
  const { currentImage } = storeToRefs(imagesStore)
  const { setImagesData, setImageReviewState } = imagesStore

  const measurementsStore = useMeasurementStore()
  const {
    setMeasurements,
    setCurrentMeasurement,
    setMeasurementUuid,
    removeMeasurementByUuid,
    getMeasurementByUuid,
    updateMeasurement,
  } = measurementsStore
  const { currentMeasurement } = storeToRefs(measurementsStore)

  const annotationsQuery = useImageAnnotationsFiltered({
    variables: {
      filterQueryParams: queryParams,
    },
    enabled: queriesEnabled,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  })

  const annotationsLoaded = computed<boolean>(() => annotationsQuery?.isSuccess?.value === true)

  const measurementQuery = useGetImageMeasurementsFiltered({
    variables: {
      filterQueryParams: { inspectionInstance: inspectionInstanceUuidParam, inspectionImage: imageUuidParam },
    },
    enabled: queriesEnabled,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  })

  const measurementsLoaded = computed<boolean>(() => measurementQuery.isSuccess.value === true)

  const imagesQuery = useGetInspectionImagesFiltered({
    variables: { filterQueryParams: imageQueryParams },
    enabled: queriesEnabled,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 150000,
  })

  const createImageAnnotation = useCreateImageAnnotationMutation({
    onSuccess: (data, variables) => handleCreateAnnotationSuccess(data, variables),
  })
  const deleteImageAnnotation = useDeleteImageAnnotationMutation({
    onSuccess: (data, variables) => handleDeleteAnnotationSuccess(variables),
  })
  const updateReviewState = useUpdateReviewStateMutation({
    onSuccess: (data, variables) => handleUpdateReviewStateSuccess(variables),
  })
  const updateImageAnnotation = useUpdateImageAnnotationMutation({
    onSuccess: (data, variables) => handleUpdateAnnotationSuccess(data, variables),
  })
  const createImageMeasurement = useCreateMeasurementMutation({
    onSuccess: handleCreateMeasurementSuccess,
  })
  const updateImageMeasurement = useUpdateMeasurementMutation()
  const deleteImageMeasurement = useDeleteMeasurementMutation({
    onSuccess: handleDeleteMeasurementSuccess,
  })
  const amsMeasurement = useImageMeasurement()
  const createAnnotationGroup = usePostImageAnnotationGroup({
    onSuccess: (data, variables) => handleCreateGroupSuccess(data, variables),
  })
  const patchImageAnnotationGroup = usePatchImageAnnotationGroup({
    onSuccess: (data, variables) => handlePatchGroupSuccess(data, variables),
  })
  const deleteImageAnnotationGroup = useDeleteAnnotationGroup({
    onSuccess: (data, variables) => handleDeleteGroupSuccess(data, variables),
  })
  const patchAnnotationsAndPostGroup = usePatchAnnotationsAndPostGroup({
    onSuccess: (data, variables) => handleCreateGroupSuccess(data, variables),
  })
  const patchAnnotationsAndPatchGroup = usePatchAnnotationsAndPatchGroup({
    onSuccess: (data, variables) => handlePatchGroupSuccess(data, variables),
  })

  function handleAnnotationBoundsUpdate(annotation: ImageAnnotationType): void {
    updateImageAnnotation.mutate({ annotationValues: [annotation], imageUuid: imageUuidParam.value as string })
  }
  function handleAnnotationDelete(type: AnnotationModalTypes): void {
    if (type === AnnotationModalTypes.Edit) {
      const annotationUuids = annotationsStore.stagedAnnotations.map((annotation) => annotation.annotationUuid)

      deleteImageAnnotation.mutate({
        annotationUuids: annotationUuids,
        annotationValues: annotationsStore.stagedAnnotations,
        imageUuid: imageUuidParam.value as string,
      })
    }
    if (type === AnnotationModalTypes.Suggestion) {
      const suggestedAnnotationValues = annotationsStore.stagedAnnotations.map((annotation) => {
        annotation.annotationStatus = AnnotationsStatuses.Rejected
        return annotation
      })
      updateImageAnnotation.mutate({
        annotationValues: suggestedAnnotationValues,
        imageUuid: imageUuidParam.value as string,
      })
    }
    if (type === AnnotationModalTypes.EditSuggestion) {
      const suggestedAnnotationValues = annotationsStore.stagedAnnotations.map((annotation) => {
        annotation.annotationStatus = AnnotationsStatuses.Rejected
        return annotation
      })
      updateImageAnnotation.mutate({
        annotationValues: suggestedAnnotationValues,
        imageUuid: imageUuidParam.value as string,
      })
    }
    if (type === AnnotationModalTypes.MultiEdit) {
      const { suggestions, annotations, annotationUuids } = annotationsStore.stagedAnnotations.reduce(
        (result, annotation) => {
          if (annotation.annotationType === 'Suggestion') {
            result.suggestions.push(annotation)
          } else {
            result.annotations.push(annotation)
            result.annotationUuids.push(annotation.annotationUuid)
          }
          return result
        },
        { suggestions: [], annotations: [], annotationUuids: [] }
      )

      if (suggestions.length > 0)
        updateImageAnnotation.mutate({
          annotationValues: suggestions.map((annotation) => {
            annotation.annotationStatus = AnnotationsStatuses.Rejected
            return annotation
          }),
          imageUuid: imageUuidParam.value as string,
        })

      deleteImageAnnotation.mutate({
        annotationUuids: annotationUuids,
        annotationValues: annotations,
        imageUuid: imageUuidParam.value as string,
      })
    }
  }

  function handleAnnotationSubmit(type: AnnotationModalTypes): void {
    if (type === AnnotationModalTypes.Add) {
      createImageAnnotation.mutate({
        annotationValues: {
          ...annotationsStore.stagedAnnotations[0],
          annotationStatus: AnnotationsStatuses.Confirmed,
          annotationType: AnnotationTypes.Annotation,
        },
        imageUuid: imageUuidParam.value as string,
      })
    }
    if (type === AnnotationModalTypes.Edit || type === AnnotationModalTypes.EditSuggestion) {
      updateImageAnnotation.mutate({
        annotationValues: annotationsStore.stagedAnnotations.map((annotation) => {
          if (annotation.annotationType === AnnotationTypes.Suggestion)
            annotation.annotationStatus = AnnotationsStatuses.Confirmed
          return annotation
        }),
        imageUuid: imageUuidParam.value as string,
      })
    }
    if (type === AnnotationModalTypes.Suggestion) {
      updateImageAnnotation.mutate({
        annotationValues: [{ ...annotationsStore.currentAnnotation, annotationStatus: AnnotationsStatuses.Confirmed }],
        imageUuid: imageUuidParam.value as string,
      })
    }
    if (type === AnnotationModalTypes.MultiEdit) {
      const existingAnnotations = []
      let tempAnnotation

      annotationsStore.stagedAnnotations.forEach((annotation) => {
        if (annotation.isTemp) tempAnnotation = annotation
        else existingAnnotations.push(annotation)
      })

      if (tempAnnotation) {
        createImageAnnotation.mutate({
          annotationValues: {
            ...tempAnnotation,
            annotationStatus: AnnotationsStatuses.Confirmed,
            annotationType: AnnotationTypes.Annotation,
          },
          imageUuid: imageUuidParam.value as string,
        })
      }
      updateImageAnnotation.mutate({
        annotationValues: existingAnnotations.map((annotation) => {
          if (annotation.annotationType === AnnotationTypes.Suggestion)
            annotation.annotationStatus = AnnotationsStatuses.Confirmed
          return annotation
        }),
        imageUuid: imageUuidParam.value as string,
      })
    }
  }

  function handleAnnotationEditAndGroupSubmit(): void {
    const existingAnnotations = annotationsStore.stagedAnnotations

    const existingGroup = annotationsStore.stagedAnnotations.find(
      (annotation) => annotation.annotationGroup !== undefined
    )?.annotationGroup

    const updatedAnnotationValues = existingAnnotations.map((annotation) => {
      if (annotation.annotationType === AnnotationTypes.Suggestion)
        annotation.annotationStatus = AnnotationsStatuses.Confirmed
      return annotation
    })

    if (existingGroup) {
      patchAnnotationsAndPatchGroup.mutate({
        annotations: updatedAnnotationValues,
        imageUuid: imageUuidParam.value as string,
        groupId: existingGroup,
      })
    } else {
      patchAnnotationsAndPostGroup.mutate({
        annotations: updatedAnnotationValues,
        imageUuid: imageUuidParam.value as string,
      })
    }
  }

  function handleGroupSubmit() {
    const existingGroup = annotationsStore.stagedAnnotations.find(
      (annotation) => annotation.annotationGroup !== undefined
    )?.annotationGroup

    // Pre-process annotations to auto-confirm suggestions
    const annotationsToSubmit = annotationsStore.stagedAnnotations.map((annotation) => {
      if (annotation.annotationType === AnnotationTypes.Suggestion) {
        // Clone to avoid mutating state directly before API return (optional but safer)
        return {
          ...annotation,
          annotationStatus: AnnotationsStatuses.Confirmed,
          isAnnotation: true, // Ensure flag matches status
        }
      }
      return annotation
    })

    if (existingGroup) {
      patchImageAnnotationGroup.mutate({
        annotations: annotationsStore.stagedAnnotations,
        groupId: existingGroup,
        imageUuid: imageUuidParam.value as string,
      })
    } else {
      createAnnotationGroup.mutate({
        annotations: annotationsStore.stagedAnnotations,
        imageUuid: imageUuidParam.value as string,
      })
    }
    annotationsStore.clearStagedAnnotations()
  }

  function handleRemoveGroupSubmit() {
    const stagedAnnotations = annotationsStore.stagedAnnotations
    const groupedAnnotationsMap = annotationsStore.groupedAnnotationsMap
    const groupIds = stagedAnnotations
      .map((annotation) => annotation.annotationGroup)
      .filter((groupId) => groupId !== undefined)

    groupIds.forEach((groupId) => {
      const annotationsInGroup = groupedAnnotationsMap[groupId]
      const stagedAnnotationsInGroup = stagedAnnotations.filter((annotation) => annotation.annotationGroup === groupId)

      const isAllItemsRemovedFromGroup = annotationsInGroup.every((annotation) =>
        stagedAnnotationsInGroup.some((_annotation) => _annotation.annotationUuid === annotation.annotationUuid)
      )
      if (isAllItemsRemovedFromGroup) {
        deleteImageAnnotationGroup.mutate({ groupId: groupId, imageUuid: imageUuidParam.value as string })
      } else {
        const inverseStagedAndGroupedAnnotations = annotationsInGroup.filter(
          (annotation) =>
            !stagedAnnotationsInGroup.some((_annotation) => _annotation.annotationUuid === annotation.annotationUuid)
        )
        patchImageAnnotationGroup.mutate({
          annotations: inverseStagedAndGroupedAnnotations,
          groupId: groupId,
          imageUuid: imageUuidParam.value as string,
        })
      }
    })

    annotationsStore.clearStagedAnnotations()
  }

  function handleImageReviewed(): void {
    const approved = currentImage.value.reviewState === ImageReviewStates.Complete
    const imageUuid = currentImage.value.imageUuid
    updateReviewState.mutate({
      imageUuid: imageUuid,
      reviewState: approved === false ? ImageReviewStates.Complete : ImageReviewStates.Review,
    })
  }

  function handleMeasurementSubmit(): void {
    const isTemp = currentMeasurement.value?.measurementUuid?.includes('temp')
    const imageUuid = currentImage.value.imageUuid

    if (isTemp) {
      createImageMeasurement.mutate({
        measurementValues: { ...currentMeasurement.value },
        points: currentMeasurement.value?.points,
        imageUuid: imageUuid,
        annotationUuid: annotationsStore.currentAnnotation?.annotationUuid,
      })
    } else {
      updateImageMeasurement.mutate({
        measurementValues: {
          ...currentMeasurement.value,
        },
        annotationUuid: annotationsStore.currentAnnotation?.annotationUuid,
        points: currentMeasurement.value?.points,
      })
    }

    currentMeasurement.value.isPending = true
  }

  function handleMeasurementDelete(): void {
    deleteImageMeasurement.mutate({
      measurementValues: {
        ...currentMeasurement.value,
      },
    })
    currentMeasurement.value.isDeleting = true
  }

  function handleCreateAnnotationSuccess(data, variables): void {
    const imageUuid = variables.imageUuid
    if (!inspectionImageIsEqual(imageUuid)) return

    let parsedData
    try {
      parsedData = JSON.parse(data)
    } catch (e) {
      console.error('Failed to parse return body of updateImageAnnotationMutation')
    }

    const originalAnnotationUuid = variables.annotationValues.annotationUuid
    const newAnnotationUuid = parsedData.image_annotation
    const newAnnotationIdentifier = parsedData.identifier

    ElNotification({
      title: 'Success',
      message: `New discrepancy with an id of ${newAnnotationIdentifier} has been created.`,
      type: 'success',
      position: 'top-left',
    })

    annotationsStore.addNewAnnotation({
      ...variables.annotationValues,
      annotationUuid: newAnnotationUuid,
      annotationId: parsedData.image_annotation_id,
      identifier: newAnnotationIdentifier,
      isTemp: false,
      isNew: true,
      tempAnnotationUuid: originalAnnotationUuid,
    })
  }

  function handleDeleteAnnotationSuccess(annotationDetailsObj): void {
    const { annotationUuids, annotationValues: deletedAnnotationValues, imageUuid } = annotationDetailsObj
    if (!inspectionImageIsEqual(imageUuid)) return

    const deletedAnnotationsIdsString = deletedAnnotationValues.map((annotation) => annotation.identifier).join(', ')

    ElNotification({
      title: 'Success',
      message: `Discrepancy ${deletedAnnotationsIdsString} has been deleted.`,
      type: 'success',
      position: 'top-left',
    })

    annotationUuids.forEach((uuid) => {
      annotationsStore.markAnnotationForDeletion(uuid)
    })
  }

  function handleUpdateAnnotationSuccess(data, variables): void {
    const { annotationValues: updatedAnnotationValues, imageUuid } = variables
    if (!inspectionImageIsEqual(imageUuid)) return

    const updatedAnnotationsIdsString = updatedAnnotationValues.map((annotation) => annotation.identifier).join(', ')
    const successMessage =
      updatedAnnotationValues?.length > 1
        ? `Discrepancies ${updatedAnnotationsIdsString} have been updated.`
        : `Discrepancy ${updatedAnnotationsIdsString} has been updated.`

    //TODO Could update backend to return all updated annotation values
    //rather than updated computed values on the frontend - as computed values are also
    //calced on the backend
    updatedAnnotationValues.forEach((annotation) => {
      if (annotation.annotationType === AnnotationTypes.Suggestion) {
        if (annotation.annotationStatus !== AnnotationsStatuses.Confirmed) {
          annotation.isAnnotation = false
          //TODO Ideally, these would be hidden instead of removed
          //Need to implement a "no render" logic for markers
          annotationsStore.markAnnotationForDeletion(annotation.annotationUuid)
        } else if (annotation.annotationStatus === AnnotationsStatuses.Confirmed) {
          annotation.isAnnotation = true
        }
      }
      annotation.isNew = true
    })

    ElNotification({
      title: 'Success',
      message: successMessage,
      type: 'success',
      position: 'top-left',
    })

    annotationsStore.commitUpdatedAnnotations(updatedAnnotationValues)
  }

  function handleUpdateReviewStateSuccess(variables): void {
    const imageUuid = variables.imageUuid
    const newReviewState =
      variables.reviewState === ImageReviewStates.Complete ? ImageReviewStates.Complete : ImageReviewStates.Review
    setImageReviewState(imageUuid, newReviewState)
  }

  function handleCreateMeasurementSuccess(data, variables) {
    const tempMeasurementUuid = variables.measurementValues.measurementUuid
    const newUuid = data.measurementUuid
    setMeasurementUuid(tempMeasurementUuid, newUuid)
    currentMeasurement.value = { ...currentMeasurement.value, measurementUuid: newUuid }
  }

  function handleDeleteMeasurementSuccess(data, variables) {
    const measurementValues = variables.measurementValues
    const measurementUuid = measurementValues.measurementUuid
    const matchingMeasurement = getMeasurementByUuid(measurementUuid)
    matchingMeasurement.isDeleting = false

    removeMeasurementByUuid(measurementUuid)
  }

  function handleCreateGroupSuccess(data, variables) {
    const { annotations, imageUuid } = variables
    const { annotations: grouped, errors: failedToGroup, groupId } = data
    if (!inspectionImageIsEqual(imageUuid)) return
    const updatedAnnotationsValues = annotations
      .filter((annotation) => grouped.includes(annotation.annotationId))
      .map((annotation: ImageAnnotationType) => {
        return {
          ...annotation,
          annotationGroup: groupId,
        }
      })
    const failedToGroupValues = annotations.filter((annotation) =>
      failedToGroup.invalidIds.includes(annotation.annotationId)
    )

    const updatedAnnotationsIdsString = updatedAnnotationsValues.map((annotation) => annotation.identifier).join(', ')
    const successMessage = `Discrepancies ${updatedAnnotationsIdsString} have been grouped.`

    annotationsStore.commitUpdatedAnnotations(updatedAnnotationsValues)

    ElNotification({
      title: 'Success',
      message: successMessage,
      type: 'success',
      position: 'top-left',
    })

    if (failedToGroupValues.length > 0) {
      const failedAnnotationsIdsString = failedToGroupValues.map((annotation) => annotation.identifier).join(', ')
      const errorMessage = `Discrepancies ${failedAnnotationsIdsString} have failed to be added to group.`
      console.error(errorMessage)

      ElNotification({
        title: 'Error',
        message: errorMessage,
        type: 'error',
        position: 'top-left',
      })
    }
  }

  function handlePatchGroupSuccess(data, variables) {
    const { annotations, groupId, imageUuid } = variables
    const groupedAnnotationsMap = annotationsStore.groupedAnnotationsMap
    const annotationsInGroup = groupedAnnotationsMap[groupId]

    if (!inspectionImageIsEqual(imageUuid)) return

    const annotationsAddedToGroup = annotations
      .filter(
        (annotation) =>
          !annotationsInGroup.some((_annotation) => _annotation.annotationUuid === annotation.annotationUuid)
      )
      .map((annotation: ImageAnnotationType) => {
        return {
          ...annotation,
          annotationGroup: groupId,
        }
      })
    const annotationsRemovedFromGroup = annotationsInGroup
      .filter(
        (annotation) => !annotations.some((_annotation) => _annotation.annotationUuid === annotation.annotationUuid)
      )
      .map((annotation: ImageAnnotationType) => {
        return {
          ...annotation,
          annotationGroup: undefined,
        }
      })
    const updatedAnnotationsValues = [...annotationsAddedToGroup, ...annotationsRemovedFromGroup]

    if (annotationsAddedToGroup.length > 0) {
      const successMessage = `Discrepancie(s) have been added to group G-${groupId}.`
      ElNotification({
        title: 'Success',
        message: successMessage,
        type: 'success',
        position: 'top-left',
      })
    }
    if (annotationsRemovedFromGroup.length > 0) {
      const successMessage = `Discrepancie(s) have been removed from group G-${groupId}.`
      ElNotification({
        title: 'Success',
        message: successMessage,
        type: 'success',
        position: 'top-left',
      })
    }
    annotationsStore.commitUpdatedAnnotations(updatedAnnotationsValues)
  }

  function handleDeleteGroupSuccess(data, variables) {
    const { groupId, imageUuid } = variables
    if (!inspectionImageIsEqual(imageUuid)) return

    const annotationsInDeletedGroup = annotationsStore.groupedAnnotationsMap[groupId]
    const updatedAnnotationsValues = annotationsInDeletedGroup.map((annotation) => {
      return {
        ...annotation,
        annotationGroup: undefined,
      }
    })
    const updatedAnnotationsIdsString = updatedAnnotationsValues.map((annotation) => annotation.identifier).join(', ')
    const successMessage = `Group G-${groupId} has been removed and discrepancie(s) ${updatedAnnotationsIdsString} have been removed.`

    ElNotification({
      title: 'Success',
      message: successMessage,
      type: 'success',
      position: 'top-left',
    })
    annotationsStore.commitUpdatedAnnotations(updatedAnnotationsValues)
  }

  function inspectionImageIsEqual(inspectionImageUuid: string) {
    return inspectionImageUuid === imageUuidParam.value
  }

  watch([annotationsQuery.data], () => {
    if (
      annotationsQuery.isSuccess &&
      annotationsQuery.data.value !== undefined &&
      Array.isArray(annotationsQuery.data.value)
    )
      annotationsStore.setAnnotations(clone(annotationsQuery.data.value))
  })
  watch([measurementQuery.data, annotationsQuery.data], () => {
    const isAnnotationsDataDefined = annotationsQuery.isSuccess && annotationsQuery.data.value !== undefined
    const isMeasurementDataDefined = measurementQuery.isSuccess && measurementQuery.data.value !== undefined

    if (isAnnotationsDataDefined && isMeasurementDataDefined) {
      const filteredMeasurements = measurementQuery.data.value?.filter((measurement) => {
        const { annotationUuid } = measurement
        const isBoundToAnnotation = measurement.annotationUuid !== undefined
        if (!isBoundToAnnotation) return true

        const matchingAnnotation = annotationsQuery.data.value?.find(
          (annotation) => annotation.annotationUuid === annotationUuid
        )
        if (!matchingAnnotation) return true

        const isBoundingAnnotationDeclined =
          matchingAnnotation?.isAnnotation === false &&
          matchingAnnotation?.annotationStatus !== AnnotationsStatuses.Rejected

        if (isBoundingAnnotationDeclined) return false
        return true
      })

      setMeasurements(filteredMeasurements)
    }
  })
  watch([imagesQuery.data], () => {
    if (imagesQuery.isSuccess && imagesQuery.data.value !== undefined && Array.isArray(imagesQuery.data.value))
      setImagesData(imagesQuery.data.value)
  })

  watch([currentImage], () => {
    const currentImageNotStarted = currentImage.value?.reviewState === ImageReviewStates.NotStarted
    if (currentImageNotStarted === true)
      updateReviewState.mutate({
        imageUuid: currentImage.value.imageUuid,
        reviewState: ImageReviewStates.Review,
      })
  })

  watch([amsMeasurement.data], () => {
    //TODO resolve typing
    const message = amsMeasurement.data.value?.message
    const success = message?.success
    const measurementData = message?.data
    const isDiscrepancyAnnotation = measurementData?.isAnnotation === true

    if (success && measurementData !== undefined) {
      if (isDiscrepancyAnnotation) {
        annotationsStore.commitUpdatedAnnotations([
          {
            ...measurementData,
            surfaceAreaStatus: MeasurementStatus.Success,
          },
        ])
      } else {
        //Else is measurement annotations
        const updatedMeasurement = { ...measurementData, isPending: false }
        updateMeasurement(updatedMeasurement)
        if (currentMeasurement.value.measurementUuid === updatedMeasurement.measurementUuid)
          setCurrentMeasurement(updatedMeasurement)
      }
    } else {
      //TODO error handling / retry logic for failed measurements
      errorStore.pushError({
        error: {
          type: 'AMSMeasurementError',
          error: message?.error,
          data: message?.data,
        },
        message: 'Failed to calculate measurement',
      })
    }
  })

  return {
    createImageAnnotation,
    updateImageAnnotation,
    deleteImageAnnotation,
    createImageMeasurement,
    updateImageMeasurement,
    deleteImageMeasurement,
    annotationsLoaded,
    measurementsLoaded,
    handleAnnotationBoundsUpdate,
    handleAnnotationDelete,
    handleAnnotationSubmit,
    handleMeasurementSubmit,
    handleMeasurementDelete,
    handleImageReviewed,
    handleGroupSubmit,
    handleAnnotationEditAndGroupSubmit,
    handleRemoveGroupSubmit,
  }
})
